// VendorProductView.jsx
import React, { useState, useEffect } from "react";
import { FiArrowLeft, FiImage, FiPackage, FiTag, FiDollarSign, FiTrendingUp, FiBox, FiLayers, FiMapPin } from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import PageView from "../../../../../../components/commonComponents/Viewpage";
import {
    useGetProductByIdQuery,
    useGetCategoriesQuery
} from "../../../../../../store/api/VendorcatalogApi";
import { useGetVendorWarehousesQuery } from "../../../../../../store/api/VendorWarehouseApi";
import { useUpdateProductStatusMutation } from "../../../../../../store/api/AdminCatalogApi";

/**
 * Product View Component - Read Only
 * Displays product details without edit options
 */
function AdminProductView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    // API Hooks
    const {
        data: productData,
        isLoading: productLoading,
        error: productError
    } = useGetProductByIdQuery(id);

    const {
        data: categoriesData,
        isLoading: categoriesLoading
    } = useGetCategoriesQuery();

    const {
        data: warehousesData,
        isLoading: warehousesLoading
    } = useGetVendorWarehousesQuery();

    // State Management
    const [product, setProduct] = useState(null);
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Initialize product data
    useEffect(() => {
        if (productData) {
            setProduct(productData);
        }
    }, [productData]);

    // Helper function to get category name by ID
    const getCategoryName = (categoryId) => {
        if (!categoriesData?.data) return `Category ${categoryId}`;
        const category = categoriesData.data.find(cat => cat.id === categoryId);
        return category ? category.name : `Category ${categoryId}`;
    };

    // Helper function to get warehouse details by ID
    const getWarehouseDetails = (warehouseId) => {
        if (!warehousesData) return null;
        const warehouse = warehousesData.find(wh => wh.id === warehouseId);
        return warehouse;
    };


    const [updateStatus, { isLoading: updating }] = useUpdateProductStatusMutation();

    const handleStatusChange = async (newStatus) => {
        try {
            await updateStatus({
                product_id: id,
                status: newStatus
            }).unwrap();


            alert(`Product ${newStatus} successfully!`);
            navigate(0); // refresh page
        } catch (error) {
            alert("Failed to update status");
        }
    };

    // Loading State
    if (productLoading || categoriesLoading || warehousesLoading) {
        return (
            <PageView title="Product Details" closeTo="/dashboard">
                <div className="flex justify-center items-center h-64">
                    <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-lg text-gray-600">Loading product details...</span>
                    </div>
                </div>
            </PageView>
        );
    }

    // Error State
    if (productError || !product) {
        return (
            <PageView title="Product Details" closeTo="/dashboard">
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <div className="text-red-600 text-lg mb-4">Error loading product details</div>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </PageView>
        );
    }

    const selectedVariant = product.variants?.[selectedVariantIndex] || {};
    const selectedImage = selectedVariant.images?.[selectedImageIndex] || {};

    return (
        <PageView title="Product Details" closeTo="/dashboard">
            <div className="w-full mx-auto space-y-6">

                {/* Header Actions */}
                <div className="flex justify-between items-center mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        <span>Back to Products</span>
                    </button>

<div className="relative inline-block text-left px-4 py-2 bg-blue-50 rounded-lg flex items-center space-x-2">
    {/* Status Dot */}
    <div
        className={`w-2 h-2 rounded-full ${
            product.status === "Approved"
                ? "bg-green-500"
                : product.status === "Rejected"
                ? "bg-red-500"
                : "bg-yellow-500"
        }`}
    ></div>

    {/* Current Status */}
    <span className="text-sm font-medium capitalize">{product.status}</span>

    {/* ✅ Only show dropdown if NOT approved */}
    {product.status !== "Approved" && (
        <div className="ml-4">
            <button
                className="px-3 py-1 text-sm bg-gray-200 rounded-md hover:bg-gray-300 transition"
                onClick={() => setOpen(!open)}
                disabled={updating}
            >
                Change
            </button>

            {open && (
                <div className="absolute mt-2 w-32 bg-white shadow-lg rounded-md border z-50">
                    <button
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => handleStatusChange("Approved")}
                        disabled={updating}
                    >
                        Approved
                    </button>
                    <button
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => handleStatusChange("Rejected")}
                        disabled={updating}
                    >
                        Rejected
                    </button>
                    <button
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => handleStatusChange("Pending")}
                        disabled={updating}
                    >
                        Pending
                    </button>
                </div>
            )}
        </div>
    )}
</div>


                </div>

                {/* Main Product Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Left Column - Images */}
                    <div className="space-y-6">

                        {/* Main Image */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <FiImage className="w-5 h-5 mr-2 text-blue-600" />
                                Product Images
                            </h3>

                            {/* Main Product Image */}
                            {product.main_image && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Main Product Image
                                    </label>
                                    <div className="relative aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden">
                                        <img
                                            src={`${process.env.REACT_APP_STORE_API_URL}${product.main_image}`}
                                            alt="Main product"
                                            className="w-full h-64 object-cover"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Variant Images */}
                            {selectedVariant.images && selectedVariant.images.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Variant Images ({selectedVariant.images.length})
                                    </label>

                                    {/* Main Selected Image */}
                                    <div className="mb-4">
                                        <div className="relative aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden">
                                            {selectedImage.image ? (
                                                <img
                                                    src={`${process.env.REACT_APP_STORE_API_URL}${selectedImage.image}`}
                                                    alt={selectedImage.alt_text}
                                                    className="w-full h-64 object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                                                    <FiImage className="w-16 h-16 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        {selectedImage.alt_text && (
                                            <p className="mt-2 text-sm text-gray-600 text-center">
                                                {selectedImage.alt_text}
                                            </p>
                                        )}
                                    </div>

                                    {/* Image Thumbnails */}
                                    {selectedVariant.images.length > 1 && (
                                        <div className="grid grid-cols-4 gap-2">
                                            {selectedVariant.images.map((image, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setSelectedImageIndex(index)}
                                                    className={`relative aspect-square bg-gray-100 rounded-md overflow-hidden border-2 ${selectedImageIndex === index ? 'border-blue-500' : 'border-transparent'
                                                        }`}
                                                >
                                                    <img
                                                        src={`${process.env.REACT_APP_STORE_API_URL}${image.image}`}
                                                        alt={image.alt_text}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Product Details */}
                    <div className="space-y-6">

                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <FiPackage className="w-5 h-5 mr-2 text-blue-600" />
                                Basic Information
                            </h3>

                            <div className="space-y-4">
                                <InfoRow label="Product Title" value={product.title} />
                                <InfoRow label="Product UPC" value={product.upc} />
                                <InfoRow
                                    label="Description"
                                    value={product.description}
                                    multiline
                                />

                                {/* Categories */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Categories
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.categories?.map((categoryId, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                            >
                                                <FiTag className="w-3 h-3 mr-1" />
                                                {getCategoryName(categoryId)}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Primary Category */}
                                {product.primary_category && (
                                    <InfoRow
                                        label="Primary Category"
                                        value={getCategoryName(product.primary_category)}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Pricing & Inventory Summary */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                Pricing & Inventory Summary
                            </h3>

                            <div className="grid grid-cols-2 gap-4">
                                <SummaryCard
                                    icon={<FiTrendingUp className="w-5 h-5" />}
                                    label="Price"
                                    value={`₹${product.min_price}`}
                                    color="text-green-600"
                                />
                                <SummaryCard
                                    icon={<FiBox className="w-5 h-5" />}
                                    label="Total Stock"
                                    value={product.total_stock?.toString()}
                                    color="text-blue-600"
                                />
                                <SummaryCard
                                    icon={<FiLayers className="w-5 h-5" />}
                                    label="Total Variants"
                                    value={product.variants?.length.toString()}
                                    color="text-purple-600"
                                />
                                <SummaryCard
                                    icon={<FiPackage className="w-5 h-5" />}
                                    label="Product Status"
                                    value={product.status}
                                    color={
                                        product.status === 'Approved' ? 'text-green-600' :
                                            product.status === 'Rejected' ? 'text-red-600' : 'text-yellow-600'
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Variants Section */}
                {product.variants && product.variants.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <FiLayers className="w-6 h-6 mr-2 text-blue-600" />
                            Product Variants
                        </h3>

                        {/* Variant Selection Tabs */}
                        {product.variants.length > 1 && (
                            <div className="mb-6 border-b border-gray-200">
                                <nav className="-mb-px flex space-x-8">
                                    {product.variants.map((variant, index) => (
                                        <button
                                            key={variant.id}
                                            onClick={() => {
                                                setSelectedVariantIndex(index);
                                                setSelectedImageIndex(0);
                                            }}
                                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${selectedVariantIndex === index
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            {variant.title || `Variant ${index + 1}`}
                                            {variant.upc && (
                                                <span className="ml-2 text-xs text-gray-400">({variant.upc})</span>
                                            )}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        )}

                        {/* Selected Variant Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                            {/* Basic Info */}
                            <div className="space-y-4">
                                <h4 className="text-lg font-medium text-gray-900">Basic Information</h4>
                                <InfoRow label="Variant Attributes" value={selectedVariant.attributes} />
                                <InfoRow label="Variant Title" value={selectedVariant.title} />
                                <InfoRow label="Variant UPC" value={selectedVariant.upc} />
                                <InfoRow label="Currency" value={selectedVariant.currency} />
                                <InfoRow label="Stock Quantity" value={selectedVariant.stock?.toString()} />
                            </div>

                            {/* Pricing */}
                            <div className="space-y-4">
                                <h4 className="text-lg font-medium text-gray-900">Pricing</h4>
                                <InfoRow label="MRP" value={`₹${selectedVariant.mrp}`} />
                                <InfoRow label="Selling Price" value={`₹${selectedVariant.price}`} />
                                <InfoRow label="Discount" value={`${selectedVariant.discount}%`} />
                                <InfoRow label="GST" value={`${selectedVariant.gst}%`} />
                                <InfoRow
                                    label="Price with GST"
                                    value={`₹${selectedVariant.price_with_gst}`}
                                    highlight
                                />
                            </div>

                            {/* Dimensions & Warehouses */}
                            <div className="space-y-4">
                                <h4 className="text-lg font-medium text-gray-900">Dimensions & Warehouses</h4>
                                <InfoRow label="Weight" value={`${selectedVariant.weight} kg`} />
                                <InfoRow label="Length" value={`${selectedVariant.length} cm`} />
                                <InfoRow label="Width" value={`${selectedVariant.width} cm`} />
                                <InfoRow label="Height" value={`${selectedVariant.height} cm`} />

                                {/* Warehouses */}
                                {selectedVariant.warehouses && selectedVariant.warehouses.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Assigned Warehouses
                                        </label>
                                        <div className="space-y-2">
                                            {selectedVariant.warehouses.map((warehouse, index) => {
                                                const warehouseDetails = getWarehouseDetails(warehouse.warehouse);
                                                return (
                                                    <div key={index} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-md">
                                                        <FiMapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                                        <div className="flex-1">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {warehouseDetails?.name || `Warehouse ${warehouse.warehouse}`}
                                                            </div>
                                                            {warehouseDetails && (
                                                                <div className="text-xs text-gray-600">
                                                                    {warehouseDetails.address}, {warehouseDetails.city}, {warehouseDetails.state}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Status */}
                                <div className="pt-4 border-t border-gray-200">
                                    <InfoRow
                                        label="Variant Status"
                                        value={selectedVariant.is_active ? 'Active' : 'Inactive'}
                                        color={selectedVariant.is_active ? 'text-green-600' : 'text-red-600'}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Variant Images Count */}
                        {selectedVariant.images && selectedVariant.images.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <InfoRow
                                    label="Total Images"
                                    value={`${selectedVariant.images.length} images`}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Product Metadata */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FiPackage className="w-5 h-5 mr-2 text-blue-600" />
                        Product Metadata
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InfoRow
                            label="Created At"
                            value={formatDate(product.created_at)}
                        />
                        <InfoRow
                            label="Updated At"
                            value={formatDate(product.updated_at)}
                        />
                        <InfoRow
                            label="Vendor ID"
                            value={product.vendor?.toString()}
                        />
                        <InfoRow
                            label="Featured Product"
                            value={product.is_featured ? 'Yes' : 'No'}
                            color={product.is_featured ? 'text-green-600' : 'text-gray-600'}
                        />
                        <InfoRow
                            label="Active Status"
                            value={product.is_active ? 'Active' : 'Inactive'}
                            color={product.is_active ? 'text-green-600' : 'text-red-600'}
                        />
                    </div>
                </div>
            </div>
        </PageView>
    );
}

// Helper Components
const InfoRow = ({ label, value, multiline = false, highlight = false, color = "text-gray-900" }) => {
    if (!value && value !== 0) return null;

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <div className={`text-sm ${color} ${highlight ? 'font-semibold' : ''} ${multiline ? 'whitespace-pre-wrap' : ''
                }`}>
                {value || '-'}
            </div>
        </div>
    );
};

const SummaryCard = ({ icon, label, value, color = "text-gray-900" }) => (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
        <div className={`flex justify-center mb-2 ${color}`}>
            {icon}
        </div>
        <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
        <div className={`text-lg font-semibold ${color}`}>{value}</div>
    </div>
);

// Helper Functions
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export default AdminProductView;